import { useEffect, useRef } from 'react';
import useSWR from 'swr';
import { useAuth } from '@teko/auth-react';
import { logger } from '@teko/log';
import { useTenantConfig } from 'contexts/GlobalDataContext';
import { useServices } from 'contexts/ServiceContext';
import { useLoyalty } from 'contexts/LoyaltyProvider';
import { LoyaltyMember } from '@teko/loyalty';

export const useLoyaltyMemberInfo = (triggerFetching?: boolean) => {
  const { needToCheckLoyaltyMemberId, notFetchLoyaltyMemberInfoGlobally } =
    useTenantConfig();
  const { user } = useAuth();
  const { loyaltyBFFService } = useServices();
  const {
    loyaltyMemberInfo: loyaltyMemberInfoCtx,
    loading: loadingCtx,
    setTriggerGetMemberInfo,
  } = useLoyalty();

  const hasFetchedRef = useRef(false);

  const shouldFetchLoyaltyInfo = !!needToCheckLoyaltyMemberId
    ? user?.telephone && user?.loyaltyMemberId
    : user?.telephone;

  const {
    data: loyaltyMemberInfo,
    isValidating,
    mutate,
  } = useSWR<LoyaltyMember | undefined>(
    shouldFetchLoyaltyInfo ? [`/member`, user?.telephone] : null,
    async () => {
      try {
        const loyaltyClientId = loyaltyBFFService.getClientId();
        if (loyaltyClientId) {
          return await loyaltyBFFService.getLoyaltyMemberInfo();
        }
      } catch (e) {
        logger.error(e, 'Error fetching loyalty member info');
        return undefined;
      }
    },
    {
      revalidateOnFocus: false,
      revalidateOnMount: false,
      revalidateIfStale: false,
      shouldRetryOnError: false,
    },
  );

  useEffect(() => {
    // only call API If have no data (!loyaltyMemberInfo)
    // AND not fetching data (!isValidating)
    // AND triggerFetching is true (triggerFetching is passed from the component)
    // AND hasFetchedRef is false (to prevent multiple calls)
    if (
      notFetchLoyaltyMemberInfoGlobally &&
      !!user?.telephone &&
      !hasFetchedRef.current &&
      !loyaltyMemberInfo &&
      !isValidating &&
      !!triggerFetching
    ) {
      hasFetchedRef.current = true;
      mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.telephone]);

  const refetchLoyaltyMemberInfo = () => {
    if (notFetchLoyaltyMemberInfoGlobally) {
      mutate();
    } else {
      setTriggerGetMemberInfo((prev) => !prev);
    }
  };

  return {
    loyaltyMemberInfo: notFetchLoyaltyMemberInfoGlobally
      ? loyaltyMemberInfo
      : loyaltyMemberInfoCtx,
    loading: notFetchLoyaltyMemberInfoGlobally ? isValidating : loadingCtx,
    refetchLoyaltyMemberInfo,
  };
};
